import Meyda, { type MeydaAudioFeature, type MeydaFeaturesObject } from 'meyda';
import { ACOUSTIC_DEFAULTS } from './config';
import { loudnessNormalizedFromDb } from './loudnessScale';
import type { LowLevelFrameFeatures } from './types';
import { clamp01, normalizedEntropy } from './utils';

const FEATURE_LIST: MeydaAudioFeature[] = [
  'rms',
  'loudness',
  'spectralFlatness',
  'spectralCentroid',
  'mfcc',
  'powerSpectrum',
  'zcr'
];

function calcPeakDensity(samples: Float32Array, threshold = ACOUSTIC_DEFAULTS.peakThreshold): number {
  if (!samples.length) return 0;
  let peaks = 0;
  for (let i = 0; i < samples.length; i += 1) {
    if (Math.abs(samples[i]) >= threshold) peaks += 1;
  }
  return peaks / samples.length;
}

function calcCrestFactor(samples: Float32Array, rms: number): number {
  if (!samples.length || rms <= 1e-8) return 0;
  let peak = 0;
  for (let i = 0; i < samples.length; i += 1) {
    peak = Math.max(peak, Math.abs(samples[i]));
  }
  return peak / rms;
}

function dbToPowerSpectrum(frequencyDb: Float32Array): Float32Array {
  const spectrum = new Float32Array(frequencyDb.length);
  for (let i = 0; i < frequencyDb.length; i += 1) {
    spectrum[i] = 10 ** (frequencyDb[i] / 10);
  }
  return spectrum;
}

function calcSpectralFlux(current: Float32Array, previous: Float32Array | null): number {
  if (!previous || previous.length !== current.length) return 0;
  let sum = 0;
  for (let i = 0; i < current.length; i += 1) {
    const diff = current[i] - previous[i];
    if (diff > 0) sum += diff;
  }
  return sum / current.length;
}

export class LowLevelFeatureExtractor {
  private previousSignal: Float32Array | null = null;

  private previousPowerSpectrum: Float32Array | null = null;

  private configured = false;

  configure(sampleRate: number, bufferSize: number = ACOUSTIC_DEFAULTS.fftSize): void {
    if (this.configured && Meyda.sampleRate === sampleRate && Meyda.bufferSize === bufferSize) return;
    Meyda.sampleRate = sampleRate;
    Meyda.bufferSize = bufferSize;
    Meyda.numberOfMFCCCoefficients = 13;
    this.configured = true;
  }

  extract(timeDomain: Float32Array, frequencyDb: Float32Array, sampleRate: number): LowLevelFrameFeatures {
    this.configure(sampleRate, timeDomain.length);
    let extracted: Partial<MeydaFeaturesObject> = {};
    try {
      extracted =
        (Meyda.extract(FEATURE_LIST, timeDomain, this.previousSignal ?? undefined) as Partial<MeydaFeaturesObject> | null) ||
        {};
    } catch {
      extracted = {};
    }
    this.previousSignal = timeDomain.slice();

    const rms = typeof extracted.rms === 'number' ? extracted.rms : 0;
    const loudnessTotal =
      typeof extracted.loudness === 'object' && extracted.loudness
        ? (extracted.loudness.total ?? 0)
        : 0;
    const powerSpectrum =
      extracted.powerSpectrum instanceof Float32Array
        ? extracted.powerSpectrum
        : dbToPowerSpectrum(frequencyDb);
    const spectralFlux = calcSpectralFlux(powerSpectrum, this.previousPowerSpectrum);
    this.previousPowerSpectrum = powerSpectrum.slice();
    const spectralEntropy = normalizedEntropy(Array.from(powerSpectrum));
    const peakDensity = calcPeakDensity(timeDomain);
    const crestFactor = calcCrestFactor(timeDomain, rms);
    const levelDb = 20 * Math.log10(Math.max(rms, 0.000_001));
    const isBelowNoiseGate = levelDb <= ACOUSTIC_DEFAULTS.noiseGateDb;
    const gatedSpectrum = isBelowNoiseGate ? new Float32Array(powerSpectrum.length) : powerSpectrum;
    const gatedFlux = isBelowNoiseGate ? 0 : spectralFlux;
    const gatedFlatness = isBelowNoiseGate ? 0 : typeof extracted.spectralFlatness === 'number' ? extracted.spectralFlatness : 0;
    const gatedCentroid = isBelowNoiseGate ? 0 : typeof extracted.spectralCentroid === 'number' ? extracted.spectralCentroid : 0;
    const gatedMfcc = isBelowNoiseGate ? [] : Array.isArray(extracted.mfcc) ? extracted.mfcc : [];
    const gatedZcr = isBelowNoiseGate ? 0 : typeof extracted.zcr === 'number' ? extracted.zcr : 0;
    const gatedEntropy = isBelowNoiseGate ? 0 : spectralEntropy;
    const gatedPeakDensity = isBelowNoiseGate ? 0 : peakDensity;
    const gatedCrestFactor = isBelowNoiseGate ? 0 : crestFactor;
    const gatedLoudnessTotal = isBelowNoiseGate ? 0 : loudnessTotal;
    const gatedLoudnessNormalized = isBelowNoiseGate ? 0 : loudnessNormalizedFromDb(levelDb);
    const gatedRms = isBelowNoiseGate ? 0 : rms;

    return {
      rms: gatedRms,
      loudnessTotal: gatedLoudnessTotal,
      spectralFlux: gatedFlux,
      spectralFlatness: gatedFlatness,
      spectralCentroid: gatedCentroid,
      mfcc: gatedMfcc,
      powerSpectrum: gatedSpectrum,
      zcr: gatedZcr,
      spectralEntropy: gatedEntropy,
      crestFactor: gatedCrestFactor,
      peakDensity: gatedPeakDensity,
      levelDb,
      loudnessNormalized: gatedLoudnessNormalized
    };
  }

  reset(): void {
    this.previousSignal = null;
    this.previousPowerSpectrum = null;
  }
}
